import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { PlusIcon as Plus, TrashIcon as Trash, PaperclipIcon as Paperclip, FilePdfIcon as FilePdf, ArrowSquareOutIcon as ArrowSquareOut } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError, photoUrl } from "@/lib/utils-app";

/**
 * Reusable per-section list (Titles / Awards / Health / Ownership) with
 * inline file-attachment support.
 *
 * `attachmentKind` (e.g. "titles", "health-records") is the URL segment
 * we POST/DELETE attachments to. Pass it to enable the paperclip button
 * + thumbnail strip on each row. Omit it for sub-lists where attachments
 * don't make sense (e.g. heat cycles).
 */
export default function SubList({
  title,
  items,
  fields,
  render,
  onAdd,
  onDelete,
  addLabel,
  dogId,
  attachmentKind,
  canEdit = true,
  onAttachmentsChanged,
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFileDesc, setPendingFileDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const dialogFileRef = React.useRef(null);

  const submit = async () => {
    // Validate required fields client-side BEFORE hitting the API.
    // This prevents the Sentry-flagged Pydantic 422s ("description
    // Field required") that surface to the user as an opaque error
    // toast and to Sentry as a "ValidationError" you can't tell from
    // a real bug.
    const missing = (fields || [])
      .filter((f) => f.required)
      .filter((f) => !String(form?.[f.name] ?? "").trim());
    if (missing.length) {
      toast.error(
        `Please fill: ${missing.map((f) => f.label).join(", ")}`,
      );
      return;
    }
    setSaving(true);
    try {
      // Create the row first so the file attachment has an item_id
      // to bind to. If this throws (e.g. missing required field), the
      // toast inside onAdd handles UX — we bail without touching the
      // file.
      const created = await onAdd(form);
      // Then upload the optional file, if any. We don't fail the
      // whole flow if the upload fails — the row is already saved
      // and the user can retry the attachment from the row.
      if (pendingFile && created?.id && attachmentKind && dogId) {
        try {
          const fd = new FormData();
          fd.append("file", pendingFile);
          if (pendingFileDesc.trim()) fd.append("description", pendingFileDesc.trim());
          await api.post(
            `/dogs/${dogId}/${attachmentKind}/${created.id}/attachments`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          toast.success("File attached.");
          onAttachmentsChanged?.();
        } catch (e) {
          toast.error(`Saved, but file upload failed: ${formatApiError(e)}`);
        }
      }
      setForm({});
      setPendingFile(null);
      setPendingFileDesc("");
      setOpen(false);
    } catch {
      /* onAdd already toasted */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-[0_8px_30px_rgba(90,107,86,0.04)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-spectral text-xl">{title}</h3>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
                size="sm"
                data-testid={`add-${title.toLowerCase().replace(/ /g, "-")}-btn`}
              >
                <Plus size={14} className="mr-1" /> {addLabel}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{addLabel}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {fields.map((f) => (
                  <div key={f.name}>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                      {f.label} {f.required && <span className="text-crimson">*</span>}
                    </Label>
                    <Input
                      type={f.type || "text"}
                      value={form[f.name] || ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      className="mt-1 rounded-lg"
                      data-testid={`sub-field-${f.name}`}
                    />
                  </div>
                ))}
                {attachmentKind && (
                  <div className="border border-stone-200 rounded-xl p-3 bg-stone-50/60 space-y-2.5" data-testid="sub-add-file-block">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] m-0">
                        Attach file <span className="text-[10px] text-[#9CA3AF] font-normal normal-case tracking-normal">(optional)</span>
                      </Label>
                      {pendingFile ? (
                        <button
                          type="button"
                          onClick={() => { setPendingFile(null); setPendingFileDesc(""); }}
                          className="text-[11px] text-[#C1272D] hover:underline"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    {pendingFile ? (
                      <>
                        <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-3 py-2 text-[12px] text-ink">
                          <Paperclip size={12} weight="bold" className="text-gold-dark flex-shrink-0" />
                          <span className="truncate font-medium" title={pendingFile.name}>
                            {pendingFile.name}
                          </span>
                          <span className="ml-auto text-[10.5px] text-[#6B6B6B] flex-shrink-0">
                            {(pendingFile.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                        <Input
                          type="text"
                          placeholder="Optional description (e.g. OFA-good · 2024)"
                          value={pendingFileDesc}
                          onChange={(e) => setPendingFileDesc(e.target.value)}
                          className="h-8 text-[12px]"
                          data-testid="sub-add-file-desc"
                        />
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => dialogFileRef.current?.click()}
                        className="w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-dashed border-stone-300 text-stone-700 text-[12px] font-medium hover:border-ink hover:bg-white active:scale-[0.99] transition-all"
                        data-testid="sub-add-pick-file"
                      >
                        <Paperclip size={12} weight="bold" />
                        Choose photo or PDF (up to 10MB)
                      </button>
                    )}
                    <input
                      ref={dialogFileRef}
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 10 * 1024 * 1024) {
                          toast.error("File must be under 10MB.");
                          e.target.value = "";
                          return;
                        }
                        setPendingFile(f);
                        e.target.value = "";
                      }}
                      data-testid="sub-add-file-input"
                    />
                    <p className="text-[10px] text-[#9CA3AF] italic">
                      Saved with this record. DNA panels, certificates, ribbons, bills of sale, etc.
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={saving}
                  className="bg-[#0E0E0E] hover:bg-[#C1272D] rounded-full"
                  data-testid="sub-save-btn"
                >
                  {saving ? "Saving…" : pendingFile ? "Save & upload" : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[#6B6B6B] text-center py-6">No records yet.</div>
      ) : (
        <ul className="divide-y divide-[#E5E5E5]">
          {items.map((it) => (
            <li key={it.id} className="py-3" data-testid={`sub-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">{render(it)}</div>
                {canEdit && (
                  <button
                    onClick={() => onDelete(it.id)}
                    className="text-crimson hover:opacity-70 mt-0.5"
                    data-testid={`del-sub-${it.id}`}
                    aria-label="Delete record"
                  >
                    <Trash size={16} />
                  </button>
                )}
              </div>
              {attachmentKind && (
                <AttachmentStrip
                  item={it}
                  dogId={dogId}
                  kind={attachmentKind}
                  canEdit={canEdit}
                  onChange={onAttachmentsChanged}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Inline attachment manager — shows existing files as thumbnails (or
 * red "PDF" tiles), with a paperclip button to upload new ones. The
 * uploader supports drag-or-pick of a single image or PDF up to 10MB,
 * with an optional description (e.g. "OFA-good · cleared 2024-03").
 */
function AttachmentStrip({ item, dogId, kind, canEdit, onChange }) {
  const attachments = item.attachments || [];
  const [busy, setBusy] = useState(false);
  const [pendingDescription, setPendingDescription] = useState("");
  const [showDescField, setShowDescField] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (pendingDescription) fd.append("description", pendingDescription);
      await api.post(
        `/dogs/${dogId}/${kind}/${item.id}/attachments`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("File attached.");
      setPendingDescription("");
      setShowDescField(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (att) => {
    if (!window.confirm(`Remove ${att.filename || "this file"}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/dogs/${dogId}/${kind}/${item.id}/attachments/${att.id}`);
      toast.success("Attachment removed.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 pl-0 sm:pl-2">
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`attachments-${item.id}`}>
          {attachments.map((a) => (
            <AttachmentChip key={a.id} att={a} onRemove={canEdit ? handleRemove : null} />
          ))}
        </div>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Primary button: opens the OS file picker immediately on
              tap. Previous version opened a description text input
              first and the actual "Choose file" button was buried in
              the same flex row — mobile users tapped this expecting a
              picker, got a text input, and couldn't find the next step.
              Description is now opt-in via the small link below. */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white text-[12px] font-bold uppercase tracking-[0.08em] disabled:opacity-50 active:scale-[0.98] transition-all"
            data-testid={`attach-btn-${item.id}`}
          >
            <Paperclip size={12} weight="bold" />
            {busy ? "Uploading…" : "Attach file"}
          </button>
          {!showDescField && !busy && (
            <button
              type="button"
              onClick={() => setShowDescField(true)}
              className="text-[11px] text-[#6B6B6B] hover:text-ink underline decoration-dotted underline-offset-2"
              data-testid={`attach-add-desc-${item.id}`}
            >
              + Add description
            </button>
          )}
          {showDescField && (
            <>
              <Input
                type="text"
                placeholder="e.g. OFA-good · cleared 2024"
                value={pendingDescription}
                onChange={(e) => setPendingDescription(e.target.value)}
                className="h-9 text-[12px] max-w-xs flex-1 min-w-[160px]"
                data-testid={`attach-desc-${item.id}`}
              />
              <button
                type="button"
                onClick={() => { setShowDescField(false); setPendingDescription(""); }}
                className="text-[11px] text-[#6B6B6B] hover:text-crimson"
              >
                Clear
              </button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
            data-testid={`attach-file-input-${item.id}`}
          />
        </div>
      )}
    </div>
  );
}

function AttachmentChip({ att, onRemove }) {
  const isPdf = (att.content_type || "").startsWith("application/pdf")
    || (att.url || att.path || "").toLowerCase().split("?")[0].endsWith(".pdf");
  // Backend stores both `url` (may be null in some envs) and `path`.
  // Resolve to a displayable URL: prefer `url`, fall back to a
  // path-based proxy URL with the user's auth token.
  const displayUrl = att.url || photoUrl(att.path || "");
  return (
    <div className="group relative inline-flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg pl-1 pr-3 py-1 max-w-[260px]">
      <a
        href={displayUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 min-w-0"
      >
        {isPdf ? (
          <div className="h-8 w-8 rounded flex items-center justify-center bg-crimson/10 text-crimson flex-shrink-0">
            <FilePdf size={16} weight="duotone" />
          </div>
        ) : (
          <img src={displayUrl} alt={att.filename || "attachment"} className="h-8 w-8 rounded object-cover flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-[11.5px] font-semibold text-ink truncate flex items-center gap-1">
            {att.filename || "Attachment"}
            <ArrowSquareOut size={9} weight="bold" className="opacity-50 flex-shrink-0" />
          </div>
          {att.description && (
            <div className="text-[10.5px] text-[#6B6B6B] truncate">{att.description}</div>
          )}
        </div>
      </a>
      {onRemove && (
        <button
          type="button"
          onClick={() => onRemove(att)}
          className="text-crimson hover:opacity-70 ml-0.5 flex-shrink-0"
          aria-label="Remove attachment"
        >
          <Trash size={11} />
        </button>
      )}
    </div>
  );
}
